import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import FihristIpucu from './Ipucu';

/**
 * KPI KARTI — gösterge ve raporların ortak kartı.
 *
 * Değer TEK SATIRDIR ve kartın dışına taşmaz: yazı kartın genişliğiyle ölçeklenir
 * (kapsayıcı sorgusu), sığmazsa üç noktayla kesilir. Uzun tutarları çağıran
 * `formatKisa` ile kısaltır; tam tutar `tam` ile verilir (ipucu + alt satır).
 */
export type Ton = 'yesil' | 'mavi' | 'mor' | 'turuncu' | 'kirmizi' | 'gri';
export type IkonAd = 'para' | 'sepet' | 'saat' | 'cuzdan' | 'kutu' | 'terazi' | 'uyari' | 'depo' | 'kisi' | 'belge' | 'grafik'
  | 'hedef' | 'takvim' | 'yuzde' | 'iptal' | 'onay' | 'etiket' | 'fabrika' | 'yildiz';

const YOL: Record<IkonAd, ReactNode> = {
  para: <><circle cx="12" cy="12" r="9" /><path d="M9.5 8v5.2a3 3 0 0 0 6 0M8 10.2l5-1.6M8 12.8l5-1.6" /></>,
  sepet: <><path d="M3 4h2.5l2.2 10.5h9.8L19.5 7H6.6" /><circle cx="9" cy="19" r="1.4" /><circle cx="16.5" cy="19" r="1.4" /></>,
  saat: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 2" /></>,
  cuzdan: <><path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3" /><rect x="4" y="8" width="16" height="11" rx="2.2" /><path d="M16 13.5h1.5" /></>,
  kutu: <><path d="M4 8l8-4 8 4v8l-8 4-8-4z" /><path d="M4 8l8 4 8-4M12 12v8" /></>,
  terazi: <><path d="M12 4v16M7 20h10M5 7h14" /><path d="M5 7l-2.5 6a2.7 2.7 0 0 0 5 0zM19 7l-2.5 6a2.7 2.7 0 0 0 5 0z" /></>,
  uyari: <><path d="M12 4l9 15.5H3z" /><path d="M12 10v4.5M12 17.2v.3" /></>,
  depo: <><path d="M3.5 10L12 4.5 20.5 10v9.5h-17z" /><path d="M8 19.5v-6h8v6M8 16.5h8" /></>,
  kisi: <><circle cx="9" cy="8.5" r="3.2" /><path d="M3 19.5c.6-3.4 3-5 6-5s5.4 1.6 6 5" /><path d="M16 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.8c2 .6 3.2 2.200 3.5 4.700" /></>,
  belge: <><path d="M7 3.5h7l4 4V20.500H7z" /><path d="M14 3.5v4h4M9.5 12h6M9.5 15.500h6" /></>,
  grafik: <><path d="M4 4v16h16" /><path d="M8 16v-4M12 16V8M16 16v-6" /></>,
  hedef: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></>,
  takvim: <><rect x="4" y="5.5" width="16" height="14" rx="2" /><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" /></>,
  yuzde: <><path d="M6 18L18 6" /><circle cx="7.5" cy="7.5" r="2.5" /><circle cx="16.500" cy="16.500" r="2.5" /></>,
  iptal: <><circle cx="12" cy="12" r="9" /><path d="M8.800 8.800l6.400 6.400M15.200 8.800l-6.400 6.400" /></>,
  onay: <><circle cx="12" cy="12" r="9" /><path d="M8 12.300l2.800 2.800L16.200 9.500" /></>,
  etiket: <><path d="M4 4.500h7.500L20 13l-6.500 6.500L5 11z" transform="translate(-.5 0)" /><circle cx="8.300" cy="8.500" r="1.200" /></>,
  fabrika: <><path d="M3.500 20V10l5.500 3.500V10l5.500 3.500V6h6v14z" /><path d="M7.500 17h2M13 17h2" /></>,
  yildiz: <path d="M12 4l2.400 5 5.400.700-4 3.800 1 5.400L12 16.300 7.200 18.900l1-5.400-4-3.800 5.400-.700z" />,
};

export function Ikon({ ad, boyut = 18 }: { ad: IkonAd; boyut?: number }) {
  return (
    <svg width={boyut} height={boyut} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {YOL[ad]}
    </svg>
  );
}

/** (i) ipucu — fareyle üzerine gelince ya da odaklanınca açılır. */
export function Ipucu({ metin }: { metin: string }) {
  return (
    <span className="ccd-ipucu" tabIndex={0} role="note" aria-label={metin}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      i<span className="ccd-ipucu-balon" aria-hidden="true">{metin}</span>
    </span>
  );
}

export type Degisim = {
  /** Önceki döneme göre yüzde; null ise hesaplanamadı (önceki dönem boş). */
  pct: number | null;
  /** "Önceki dönem: 10,5 Mn ₺" gibi açıklama. */
  onceki?: string;
  /** Artışın KÖTÜ olduğu göstergeler (alış, gecikme): renk ters döner. */
  artisKotu?: boolean;
};

type Props = {
  ton: Ton; ikon: IkonAd; etiket: string;
  /** Büyük değer — kısa yazın ("12,8 Mn ₺", "280"). */
  deger: ReactNode;
  /** Tam değer; değerin `title` ipucunda görünür. */
  tam?: string;
  ipucu?: string;
  /** Fihrist maddesinin kimliği: «?» balonu ve sağ tuş "Bu nedir?" buna bağlanır. `ipucu` balonda ek not olur. */
  fihrist?: string;
  degisim?: Degisim;
  /** Kartın altındaki ikincil satır(lar). */
  alt?: ReactNode;
  to?: string;
  /** Değer boş/sıfır anlamına geliyorsa kart soluklaşır. */
  soluk?: boolean;
  vurgu?: boolean;
  /** Kılavuz turunun hedefi (`data-tur`). */
  tur?: string;
};

export default function KpiKart({ ton, ikon, etiket, deger, tam, ipucu, fihrist, degisim, alt, to, soluk, vurgu, tur }: Props) {
  const yon = degisim?.pct == null ? null : degisim.pct > 0 ? 'yukari' : degisim.pct < 0 ? 'asagi' : 'duz';
  const iyi = yon == null || yon === 'duz' ? null : (yon === 'yukari') !== Boolean(degisim?.artisKotu);
  const govde = (
    <>
      <div className="ccd-kpi-ust">
        <span className="ccd-kpi-ikon"><Ikon ad={ikon} /></span>
        {degisim && (
          <span className={`ccd-kpi-degisim${iyi == null ? '' : iyi ? ' ccd-kpi-iyi' : ' ccd-kpi-kotu'}`} data-fihrist="onceki-donem"
                title={degisim.onceki ?? 'Aynı uzunluktaki bir önceki döneme göre'}>
            {yon == null ? '—' : `${yon === 'yukari' ? '↗' : yon === 'asagi' ? '↘' : '→'} %${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: Math.abs(degisim.pct!) >= 100 ? 0 : 1 }).format(Math.abs(degisim.pct!))}`}
          </span>
        )}
      </div>
      <div className="ccd-kpi-deger" title={tam}>{deger}</div>
      <div className="ccd-kpi-etiket">
        <span>{etiket}</span>
        {fihrist ? <FihristIpucu id={fihrist} not={ipucu} /> : ipucu && <Ipucu metin={ipucu} />}
      </div>
      {(alt || degisim?.onceki) && (
        <div className="ccd-kpi-alt">
          {alt}
          {degisim?.onceki && <div>{degisim.onceki}</div>}
        </div>
      )}
    </>
  );
  const sinif = `ccd-kpi ccd-ton-${ton}${soluk ? ' ccd-kpi-soluk' : ''}${vurgu ? ' ccd-kpi-vurgu' : ''}`;
  return to
    ? <Link to={to} className={`${sinif} ccd-kpi-bag`} data-tur={tur} data-fihrist={fihrist}>{govde}</Link>
    : <div className={sinif} data-tur={tur} data-fihrist={fihrist}>{govde}</div>;
}

/** Kart ızgarası: geniş ekranda 4, 1100 px altında 2 sütun; kartlar eşit yükseklikte. */
export function KpiIzgara({ children, tur, uclu, besli }: { children: ReactNode; tur?: string; /** Üç kartlık sıra. */ uclu?: boolean; /** Beş kartlık sıra. */ besli?: boolean }) {
  return <div className={`ccd-kpi-izgara${uclu ? ' ccd-kpi-izgara-3' : ''}${besli ? ' ccd-kpi-izgara-5' : ''}`} data-tur={tur}>{children}</div>;
}
